// Canonical tag values for the two TAGGING systems materialized on `patient`:
//
//   patient.high_billing   text[]  — which high-value treatment types this patient
//                                    qualifies under (appointmentList.stat_type OR
//                                    billing_type.StatType, recomputed nightly)
//   patient.reimbursement  text[]  — which advance-pay payers owe on this patient
//                                    (billing.advancePay, recomputed nightly)
//
// Both are also mirrored by a `*_import` bool marking rows whose tags came from the
// one-off legacy backfill rather than the live recompute.
//
// The Recall filter (`billing_type=` / `reimbursement=`) sends ONE tag as a plain
// string; Xano matches it against the array. These lists only drive the dropdowns
// and the chip colors — a tag that arrives from the server but isn't listed here
// still renders, just in the neutral slate tone. So an out-of-date list degrades
// quietly instead of hiding data.
//
// EDIT ME: add/rename tags here when the nightly recompute's stat sets change.
// Keep `value` byte-identical to what Xano stores; `label` is display-only.

import { PAYER_GROUPS } from './reimbursementPayers.js'

// tone keys reuse the shared STATUS_TONES palette in data/patientStatuses.js
export const HIGH_BILLING_TAGS = [
  { value: 'invisalign', label: 'Invisalign', tone: 'violet' },
  { value: 'd-aligner',  label: 'D-Aligner',  tone: 'blue' },
  { value: 'implant',    label: 'Implant',    tone: 'teal' },
  { value: 'veneer',     label: 'Veneer',     tone: 'orange' },
]

// payer group → shared tone key
const PAYER_TONE = { gov: 'teal', insurance: 'violet', platform: 'orange', company: 'blue', other: 'slate' }

// payers are already curated (+ grouped) for the ตามเบิก report — reuse that list
// so the two screens can never disagree about what a payer is called.
export const REIMBURSEMENT_TAGS = PAYER_GROUPS.flatMap((g) =>
  g.payers.map((p) => ({ value: p, label: p, tone: PAYER_TONE[g.key] || 'slate', group: g.label })),
)

// Xano's StatType casing is inconsistent ("Veneer" vs "veneer", "Implant" vs
// "implant"), and payer names pick up stray spaces/dots — match the same tolerant
// way reimbursementPayers.js does so a chip never falls through on punctuation.
const norm = (s) => String(s ?? '').toLowerCase().replace(/[\s.\-]/g, '')

const LOOKUP = new Map(
  [...HIGH_BILLING_TAGS, ...REIMBURSEMENT_TAGS].map((t) => [norm(t.value), t]),
)

/** Look up a tag's display meta; unknown tags render as-is in slate. */
export function tagMeta(value) {
  return LOOKUP.get(norm(value)) || { value, label: String(value ?? ''), tone: 'slate' }
}

/** Read a tag array off a row — tolerates null, a bare string, or a JSON string. */
export function asTags(v) {
  if (!v) return []
  if (Array.isArray(v)) return v.map((x) => String(x ?? '').trim()).filter(Boolean)
  const s = String(v).trim()
  if (!s) return []
  if (s.startsWith('[')) { try { return asTags(JSON.parse(s)) } catch { /* fall through */ } }
  return [s]
}
